import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MarketingNavbar from "../MarketingNavbar/MarketingNavbar";
import MarketingFooter from "../MarketingFooter/MarketingFooter";
import { useAuth } from "../../../context/AuthContext";
import styles from "./Four04Landing.module.css";

export default function Four04Landing() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const headingRef = useRef(null);

  const reason =
    location.state?.reason ||
    (location.pathname.startsWith("/app") && !user ? "auth" : "404");

  const fromPath = location.state?.from?.pathname || null;

  const isAuth = reason === "auth";
  const title = isAuth ? "Sign in required" : "Page not found";
  const message = isAuth
    ? "You tried to open a protected page. Please sign in to continue."
    : "The page you’re looking for doesn’t exist or was moved.";

  // Pick a code for the badge
  const code = isAuth ? "401" : "404";

  const handleLogin = async () => {
    try {
      await loginWithGoogle();
      const dest = fromPath || "/app/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      // If popup closed/blocked, your AuthContext will handle redirect fallback
      console.warn("Login aborted:", err?.message || err);
    }
  };

  useEffect(() => {
    // Set document title for better UX/SEO
    document.title = `${code} · ${title} · Pocket Penny`;
    // Move focus to the heading for screen readers / keyboard users
    headingRef.current?.focus();
  }, [code, title]);

  return (
    <div className="d-flex flex-column min-vh-100 bg-body text-body">
      <MarketingNavbar />

      <main className={`flex-grow-1 ${styles.wrap}`} role="main" aria-live="polite">
        <div className="container">
          <div className={styles.card}>
            <div className={`${styles.badge}`} aria-hidden="true">{code}</div>

            <h1
              ref={headingRef}
              tabIndex={-1}
              className="h3 fw-bold mb-2"
            >
              {title}
            </h1>

            <p className="text-secondary mb-4">{message}</p>

            <div className={`${styles.hint} mb-4`} aria-label="Attempted URL">
              <span className="me-2">Attempted URL:</span>
              <code className={styles.code}>{location.pathname}</code>
            </div>

            <div className="d-flex gap-2 flex-wrap justify-content-center">
              {isAuth && !user ? (
                <>
                  <button className="btn btn-primary btn-compact" onClick={handleLogin}>
                    Login with Google
                  </button>
                  {/* helpful for private beta flows */}
                  <a href="#demo" className="btn btn-outline-secondary btn-compact">
                    Request Invite
                  </a>
                </>
              ) : (
                <>
                  <Link to="/" className="btn btn-primary btn-compact">
                    Back to Home
                  </Link>
                  {fromPath && user ? (
                    <button
                      className="btn btn-outline-secondary btn-compact"
                      onClick={() => navigate(fromPath, { replace: true })}
                    >
                      Try Again
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
